import { Autocomplete, TextField, Stack, Button } from '@mui/material';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useResource } from 'rest-hooks';
import { VariableResource } from '../../model/resources/variableResource';
import { Dialog } from '../components/dialog';

interface XmlExpressionBuilderProps {
  handleExpression: (expression: string) => void;
}

interface Expression {
  variable: string;
  compareOperator: string;
  compareValue: string;
}

const compareOperators = ['=', '<', '>', '<=', '>=', '!='];

export const XmlExpressionBuilder = (props: XmlExpressionBuilderProps) => {
  const { handleExpression } = props;

  const [modalOpen, setModalOpen] = useState(false);

  const variables = useResource(VariableResource.list(), {});
  const variableNames = useMemo(
    () => variables.map((variable) => `$${variable.name}`),
    [variables]
  );
  const [expression, setExpression] = useState<Partial<Expression>>();

  const submitExpression = () => {
    const expressionString = `${expression?.variable} ${expression?.compareOperator} ${expression?.compareValue}`;
    handleExpression(expressionString);
  };

  return (
    <>
      <Button size="small" onClick={() => setModalOpen(true)} variant="outlined">
        <FormattedMessage
          id="expression.editor.helpers.basicExpression"
          defaultMessage="Basic expression"
        />
      </Button>
      <Dialog
        title={
          <FormattedMessage
            id="basicExpression.modal.title"
            defaultMessage="Insert basic expression"
          />
        }
        content={
          <Stack direction="row">
            <Autocomplete
              value={expression?.variable}
              onChange={(_, value) =>
                setExpression((currExpression) => ({
                  ...currExpression,
                  variable: value ?? undefined
                }))
              }
              options={variableNames}
              size="small"
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField {...params} label="Variable" variant="standard" />
              )}
            />
            <Autocomplete
              value={expression?.compareOperator}
              onChange={(_, value) =>
                setExpression((currExpression) => ({
                  ...currExpression,
                  compareOperator: value ?? undefined
                }))
              }
              options={compareOperators}
              size="small"
              sx={{ width: 100 }}
              renderInput={(params) => (
                <TextField {...params} label="Operator" variant="standard" />
              )}
            />
            <Autocomplete
              freeSolo
              value={expression?.compareValue}
              onChange={(_, value) =>
                setExpression((currExpression) => ({
                  ...currExpression,
                  compareValue: value ?? undefined
                }))
              }
              options={variableNames}
              size="small"
              sx={{ width: 200 }}
              renderInput={(params) => <TextField {...params} label="Value" variant="standard" />}
            />
          </Stack>
        }
        onClose={() => setModalOpen(false)}
        open={modalOpen}
        actions={
          <>
            <Button variant="contained" color="secondary" onClick={() => setModalOpen(false)}>
              <FormattedMessage id="Cancel" />
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                submitExpression();
                setModalOpen(false);
                setExpression(undefined);
              }}>
              <FormattedMessage id="Insert" />
            </Button>
          </>
        }
      />
    </>
  );
};
